import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Container } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader, SaveBar } from '../AdminLayout'
import {
  FieldGroup,
  I18nField,
  NumberField,
  TextAreaField,
  TextField,
} from '../components/fields'
import { AlertsCard } from '../components/AlertsCard'
import { BrandEditor } from '../components/BrandEditor'
import { ReadinessCard } from '../components/ReadinessCard'
import { MediaField } from '../components/MediaField'
import { EditionsPanel } from '../components/EditionsPanel'
import {
  CurrencyField,
  LocalesField,
  TimezoneField,
  orderedLocales,
} from '../components/editionFields'
import { useAdminContext } from '../session'
import type { AdminEvent, Alerts, Readiness } from '../types'
import { fromZonedInput, toZonedInput } from '../zonedInput'

/** Ce que le formulaire édite. Sous-ensemble strict de `EventUpdate`. */
type Draft = Pick<
  AdminEvent,
  | 'name_i18n'
  | 'subtitle_i18n'
  | 'slogan_i18n'
  | 'tagline_i18n'
  | 'description_i18n'
  | 'venue_name'
  | 'address_line1'
  | 'postal_code'
  | 'city'
  | 'max_capacity'
  | 'contact_email'
  | 'contact_phone'
  | 'doors_open_at'
  | 'starts_at'
  | 'ends_at'
  | 'hero_media_id'
  | 'logo_media_id'
  | 'timezone'
  | 'currency'
  | 'locales'
  | 'default_locale'
>

const I18N_KEYS = [
  'name_i18n',
  'subtitle_i18n',
  'slogan_i18n',
  'tagline_i18n',
  'description_i18n',
] as const

/**
 * L'édition : ses textes, sa date, son lieu, et tous ses réglages.
 *
 * C'est l'écran le plus important du back-office — celui qui remplace la
 * douzaine d'appels à l'API qu'il fallait faire à la main. Deux blocs très
 * différents s'y côtoient :
 *
 * * **le formulaire de l'édition**, enregistré d'un bloc par `PATCH` ;
 * * **les réglages**, enregistrés individuellement, parce qu'ouvrir la
 *   commande à table pendant la soirée ne doit pas dépendre d'un formulaire
 *   complet valide.
 */
export function AdminEventPage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()

  const eventQuery = useAdminQuery<AdminEvent>(
    ['admin-event', eventId],
    `/admin/events/${eventId}`,
    eventId,
  )
  // Ce qui demande une décision aujourd'hui, à côté de ce qui reste à
  // préparer : la checklist dit ce qu'on n'a pas encore fait, les alertes
  // disent ce qui s'est cassé depuis.
  const alertsQuery = useAdminQuery<Alerts>(
    ['admin-alerts', eventId],
    `/admin/events/${eventId}/alerts`,
    eventId,
  )
  const brand = useAdminMutation<AdminEvent, Record<string, unknown>>(
    'patch', () => `/admin/events/${eventId}`, eventId,
    [['admin-event', eventId], ['public-event']],
  )
  const readinessQuery = useAdminQuery<Readiness>(
    ['admin-readiness', eventId],
    `/admin/events/${eventId}/readiness`,
    eventId,
  )

  const save = useAdminMutation<AdminEvent, Partial<Draft>>(
    'patch',
    () => `/admin/events/${eventId}`,
    eventId,
    [['admin-event', eventId], ['admin-readiness', eventId], ['public-event']],
  )

  const server = eventQuery.data
  const [draft, setDraft] = useState<Draft | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  // Le brouillon part de ce que le serveur a renvoyé, et n'est réinitialisé
  // que lorsqu'on n'a rien en cours : sans cette garde, un rafraîchissement
  // de fond effacerait ce qu'on est en train de taper.
  //
  // Le brouillon retient aussi l'édition dont il vient. Changer d'édition
  // (depuis le panneau « Éditions ») repart ainsi de zéro : sans cela, le
  // brouillon de l'ancienne restait affiché — et s'enregistrait — sur la
  // nouvelle.
  const [draftFor, setDraftFor] = useState<string | null>(null)
  useEffect(() => {
    if (server && (draft === null || draftFor !== server.id)) {
      setDraft(pick(server))
      setDraftFor(server.id)
      if (draftFor !== server.id) setSavedAt(null)
    }
  }, [server, draft, draftFor])

  const dirty = useMemo(
    () => Boolean(server && draft && JSON.stringify(pick(server)) !== JSON.stringify(draft)),
    [server, draft],
  )

  useEffect(() => {
    document.title = t('admin.section.event')
  }, [t])

  // Le brouillon d'une autre édition, le temps d'un rendu après le changement :
  // on attend qu'il soit remplacé plutôt que de le montrer sous le mauvais nom.
  if (!server || !draft || draftFor !== server.id) {
    return (
      <Container className="py-20">
        <p className="text-content-muted">{t('state.loading')}</p>
      </Container>
    )
  }

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => (current ? { ...current, [key]: value } : current))
  }

  // Les onglets de langue suivent les langues de l'édition : proposer de
  // traduire en anglais une édition qui ne parle pas anglais, c'est envoyer
  // un texte que le serveur refuse. `key` remonte les champs quand la liste
  // change, pour qu'aucun onglet ne reste ouvert sur une langue retirée.
  const textLocales = orderedLocales(draft.locales)
  const localesKey = textLocales.join(',')

  // Les heures se lisent et s'écrivent dans le fuseau ENREGISTRÉ de
  // l'édition — celui qu'annonce le sous-titre du groupe. Un fuseau modifié
  // mais pas encore enregistré ne s'applique qu'après l'enregistrement.
  const zone = server.timezone

  return (
    <>
      <AdminHeader
        title={t('admin.section.event')}
        description={t('admin.event.description', {
          slug: server.slug,
          year: server.edition_year,
        })}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] xl:items-start">
        <div className="grid min-w-0 gap-6">
          <EditionsPanel current={server} readiness={readinessQuery.data} />

          <FieldGroup title={t('admin.event.identity')}>
            <I18nField
              key={`name-${localesKey}`}
              label={t('admin.event.name')}
              locales={textLocales}
              value={draft.name_i18n}
              onChange={(value) => set('name_i18n', value)}
            />
            <I18nField
              key={`subtitle-${localesKey}`}
              label={t('admin.event.subtitle')}
              locales={textLocales}
              value={draft.subtitle_i18n}
              onChange={(value) => set('subtitle_i18n', value)}
            />
            <I18nField
              key={`slogan-${localesKey}`}
              label={t('admin.event.slogan')}
              locales={textLocales}
              value={draft.slogan_i18n}
              onChange={(value) => set('slogan_i18n', value)}
            />
            <I18nField
              key={`tagline-${localesKey}`}
              label={t('admin.event.tagline')}
              hint={t('admin.event.taglineHint')}
              locales={textLocales}
              value={draft.tagline_i18n}
              onChange={(value) => set('tagline_i18n', value)}
            />
            <div className="sm:col-span-2">
              <I18nField
                key={`description-${localesKey}`}
                label={t('admin.event.descriptionField')}
                multiline
                locales={textLocales}
                value={draft.description_i18n}
                onChange={(value) => set('description_i18n', value)}
              />
            </div>
          </FieldGroup>

          <FieldGroup
            title={t('admin.event.when')}
            description={t('admin.event.whenHint', { timezone: zone })}
          >
            <TextField
              label={t('admin.event.doorsOpen')}
              type="datetime-local"
              value={toZonedInput(draft.doors_open_at, zone)}
              onChange={(value) => set('doors_open_at', fromZonedInput(value, zone))}
            />
            <TextField
              label={t('admin.event.starts')}
              type="datetime-local"
              value={toZonedInput(draft.starts_at, zone)}
              onChange={(value) => set('starts_at', fromZonedInput(value, zone) ?? draft.starts_at)}
            />
            <TextField
              label={t('admin.event.ends')}
              type="datetime-local"
              value={toZonedInput(draft.ends_at, zone)}
              onChange={(value) => set('ends_at', fromZonedInput(value, zone))}
            />
            <NumberField
              label={t('admin.event.capacity')}
              hint={t('admin.event.capacityHint')}
              value={draft.max_capacity}
              min={1}
              onChange={(value) => set('max_capacity', value)}
            />
          </FieldGroup>

          {/* Ce qui ne se réglait que par l'API. Chaque indication dit la
              règle que le serveur applique : on la lit avant d'enregistrer,
              plutôt que de la découvrir dans un message d'erreur. */}
          <FieldGroup
            title={t('admin.event.localization')}
            description={t('admin.event.localizationHint')}
          >
            <TimezoneField
              label={t('admin.event.timezone')}
              hint={t('admin.event.timezoneHint')}
              value={draft.timezone}
              onChange={(value) => set('timezone', value)}
            />
            <CurrencyField
              label={t('admin.event.currency')}
              hint={t('admin.event.currencyHint')}
              value={draft.currency}
              onChange={(value) => set('currency', value)}
            />
            <LocalesField
              locales={draft.locales}
              defaultLocale={draft.default_locale}
              hint={t('admin.event.localesHint')}
              onChange={(next) =>
                setDraft((current) =>
                  current
                    ? { ...current, locales: next.locales, default_locale: next.default_locale }
                    : current,
                )
              }
            />
          </FieldGroup>

          {/* Les visuels de l'édition. Ils manquaient : le logo et la photo
              d'ambiance ne pouvaient se poser que par appel d'API, ce qui
              revenait à dire qu'ils n'étaient pas configurables. */}
          <FieldGroup title={t('admin.event.visuals')}>
            <MediaField
              label={t('admin.media.eventLogo')}
              hint={t('admin.media.eventLogoHint')}
              value={draft.logo_media_id}
              eventId={eventId}
              onChange={(value) => set('logo_media_id', value)}
            />
            <MediaField
              label={t('admin.media.eventHero')}
              hint={t('admin.media.eventHeroHint')}
              value={draft.hero_media_id}
              eventId={eventId}
              onChange={(value) => set('hero_media_id', value)}
              aspect="wide"
            />
          </FieldGroup>

          <FieldGroup title={t('admin.event.where')}>
            <TextField
              label={t('admin.event.venue')}
              value={draft.venue_name ?? ''}
              onChange={(value) => set('venue_name', value || null)}
            />
            <TextField
              label={t('admin.event.city')}
              value={draft.city ?? ''}
              onChange={(value) => set('city', value || null)}
            />
            <div className="sm:col-span-2">
              <TextAreaField
                label={t('admin.event.address')}
                rows={2}
                value={draft.address_line1 ?? ''}
                onChange={(value) => set('address_line1', value || null)}
              />
            </div>
            <TextField
              label={t('admin.event.postalCode')}
              value={draft.postal_code ?? ''}
              onChange={(value) => set('postal_code', value || null)}
            />
          </FieldGroup>

          <FieldGroup title={t('admin.event.contact')}>
            <TextField
              label={t('admin.event.contactEmail')}
              type="email"
              value={draft.contact_email ?? ''}
              onChange={(value) => set('contact_email', value || null)}
            />
            <TextField
              label={t('admin.event.contactPhone')}
              type="tel"
              value={draft.contact_phone ?? ''}
              onChange={(value) => set('contact_phone', value || null)}
            />
          </FieldGroup>

          {/* L'identité visuelle, à part du formulaire : elle s'enregistre
              seule, comme les réglages, parce qu'ajuster un titre ne doit pas
              dépendre d'un formulaire d'édition entièrement valide. */}
          {server ? (
            <BrandEditor
              key={server.id}
              name={
                server.name_i18n[i18n.language] ??
                Object.values(server.name_i18n)[0] ??
                ''
              }
              segments={server.title_style ?? []}
              theme={server.theme ?? {}}
              busy={brand.isPending}
              error={brand.isError ? brand.error.message : null}
              onSave={(payload) => brand.mutate(payload)}
            />
          ) : null}

        </div>

        <div className="space-y-6 xl:sticky xl:top-8 xl:self-start">
          <AlertsCard alerts={alertsQuery.data} />
          <ReadinessCard readiness={readinessQuery.data} />
        </div>
      </div>

      <SaveBar
        dirty={dirty}
        saving={save.isPending}
        savedAt={savedAt}
        error={save.error instanceof ApiError ? save.error.message : null}
        onReset={() => setDraft(pick(server))}
        onSave={() => {
          save.mutate(changesOf(pick(server), draft), {
            onSuccess: (updated) => {
              // On repart de ce que le serveur a réellement enregistré : il a
              // pu normaliser une date ou rogner un espace.
              setDraft(pick(updated))
              setSavedAt(Date.now())
            },
          })
        }}
      />
    </>
  )
}

function pick(event: AdminEvent): Draft {
  return {
    name_i18n: event.name_i18n,
    subtitle_i18n: event.subtitle_i18n,
    slogan_i18n: event.slogan_i18n,
    tagline_i18n: event.tagline_i18n,
    description_i18n: event.description_i18n,
    venue_name: event.venue_name,
    address_line1: event.address_line1,
    postal_code: event.postal_code,
    city: event.city,
    max_capacity: event.max_capacity,
    contact_email: event.contact_email,
    contact_phone: event.contact_phone,
    doors_open_at: event.doors_open_at,
    starts_at: event.starts_at,
    ends_at: event.ends_at,
    hero_media_id: event.hero_media_id,
    logo_media_id: event.logo_media_id,
    timezone: event.timezone,
    currency: event.currency,
    locales: event.locales,
    default_locale: event.default_locale,
  }
}

/**
 * Seulement ce qui a changé — et chaque texte réduit aux langues retenues.
 *
 * Envoyer le formulaire entier ne marchait plus dès qu'on retire une langue :
 * les textes gardent leur version dans la langue retirée (masquée, pas
 * effacée), et le serveur refuse tout texte qui en porte une qu'il ne déclare
 * pas. Renvoyer un texte inchangé suffisait donc à bloquer l'enregistrement,
 * pour un champ qu'on n'avait pas touché.
 *
 * Un texte qu'on modifie, lui, part réduit aux langues de l'édition : il perd
 * alors sa version masquée — c'est le seul moyen que le serveur l'accepte, et
 * l'indication du champ « Langues » le dit.
 */
function changesOf(before: Draft, after: Draft): Partial<Draft> {
  const changes: Partial<Draft> = {}
  for (const key of Object.keys(after) as (keyof Draft)[]) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      Object.assign(changes, { [key]: after[key] })
    }
  }
  for (const key of I18N_KEYS) {
    const text = changes[key]
    if (text) {
      changes[key] = Object.fromEntries(
        Object.entries(text).filter(([locale]) => after.locales.includes(locale)),
      )
    }
  }
  if (changes.timezone !== undefined) changes.timezone = changes.timezone.trim()
  return changes
}
